"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBranchCostBreakdown = exports.toLocalDateString = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const Sensor_1 = require("../../types/Sensor");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const costSeries_1 = require("../../utils/energy/costSeries");
const energyRate_1 = require("../../utils/energyRate");
const pacificTime_1 = require("../../utils/pacificTime");
const useHVACSensorsByGroup_1 = require("../hvac/useHVACSensorsByGroup");
const useStableList_1 = require("../useStableList");
// Matches useBranchEnergyCalculation / useElectricalWithHVAC: a configured circuit voltage at or
// above this is split-phase — the CT clamps one leg, so the load voltage is ~2x the leg reading.
const SPLIT_PHASE_VOLTAGE_THRESHOLD = 180;
// Wide enough for the widgets' day/week/month navigation (current month + two full
// months back) without refetching. ~95 days of hourly rollups per monitor is one
// paginated fetch on mount, not a per-navigation cost.
const DEFAULT_DAYS = 95;
const toLocalDateString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
exports.toLocalDateString = toLocalDateString;
/**
 * Per-branch-circuit energy cost, sliceable by date range, from the hourly
 * `SensorUsageRollup` rows the mains cost chart already reads. Branch kWh uses the same
 * conventions as the live branch views: inputs 1-7 ride leg 1 (vt1), inputs 8-16 ride
 * leg 2 (vt2), and circuits configured as split-phase double the leg voltage. HVAC groups
 * get their own rows from the SEER daily aggregates (`powerWattHours`) since their draw
 * flows through the mains but isn't clamped by any branch CT. The mains integral is the
 * denominator, so `other` is everything the panel drew that isn't attributed to a branch
 * CT or an HVAC system.
 */
const useBranchCostBreakdown = (sensors, selectedHubId, days = DEFAULT_DAYS, hubConfig) => {
    const [rollupsBySensor, setRollupsBySensor] = (0, react_1.useState)({});
    const [hvacDailyRows, setHvacDailyRows] = (0, react_1.useState)([]);
    const [hvacHourlyRows, setHvacHourlyRows] = (0, react_1.useState)([]);
    const [hvacHourlyOk, setHvacHourlyOk] = (0, react_1.useState)(false);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Stable until a power monitor itself changes: the 95-day daily table below is keyed on it.
    const powerMonitorSensors = (0, useStableList_1.useStableList)(sensors?.filter((sensor) => sensor.type === Sensor_1.SensorType.POWER && sensor.hubId === selectedHubId) ?? []);
    // HVAC condenser/air handler pairs draw through the same panel mains but are monitored by
    // their own CTs, so without this they'd all land in "other". Same household group source as
    // the live branch list (useElectricalWithHVAC).
    const { hvacSensorGroups } = (0, useHVACSensorsByGroup_1.useHVACSensorsByGroup)(selectedHubId ?? undefined, sensors);
    const sensorIdsKey = powerMonitorSensors.map((s) => s.id).join(',');
    const groupIdsKey = hvacSensorGroups.map((g) => g.groupId).join(',');
    const ratePerKwh = (0, react_1.useMemo)(() => (0, energyRate_1.getRatePerKwh)(hubConfig), [
        hubConfig ? JSON.stringify(hubConfig) : null,
    ]);
    const todayDate = (0, exports.toLocalDateString)(new Date());
    const earliestDate = (0, react_1.useMemo)(() => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return (0, exports.toLocalDateString)(date);
    }, [days, todayDate]);
    const fetchRollupForSensor = async (id) => {
        const allData = [];
        let nextToken;
        do {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: { sensorId: id, startDate: earliestDate, endDate: todayDate, nextToken },
            });
            const response = result?.data?.getSensorUsageRollup;
            const pageData = response?.items;
            if (pageData?.length > 0)
                allData.push(...pageData);
            nextToken = response?.nextToken;
        } while (nextToken);
        return allData;
    };
    // Daily `powerWattHours` rows (from the SEER daily aggregates — deduped condenser+air-handler
    // energy while running). Queried per owning hub because the aggregates are hub-partitioned and
    // a gateway's group lives under the gateway, not the hub showing the list. Failures degrade to
    // "no HVAC rows" (the energy stays in "other") rather than blanking the breakdown.
    const fetchHvacDailyRows = async () => {
        if (hvacSensorGroups.length === 0)
            return [];
        const hubIds = Array.from(new Set(hvacSensorGroups
            .map((g) => g.hubId || selectedHubId)
            .filter((id) => Boolean(id))));
        const results = await Promise.all(hubIds.map(async (hubId) => {
            try {
                const response = await amplifyBootstrap_1.API.graphql({
                    query: queries_1.getDailyHvacAggregates,
                    variables: { hubId, startDate: earliestDate, endDate: todayDate },
                });
                return (response?.data?.getDailyHvacAggregates ?? []);
            }
            catch (err) {
                console.error(`Error fetching daily HVAC aggregates for hub ${hubId}:`, err);
                return [];
            }
        }));
        const knownGroupIds = new Set(hvacSensorGroups.map((g) => g.groupId));
        return results.flat().filter((row) => row?.groupId && knownGroupIds.has(row.groupId));
    };
    // Hourly HVAC energy (SEER_RATING records) over the whole window. Two jobs: the hour bars
    // of the Day chart, and the days whose daily aggregate hasn't landed yet (today, and
    // yesterday until ~01:30 PT) — without it that A/C energy sits in "other".
    // Returns null when every query failed (resolver not deployed / errored) so the caller can
    // distinguish "A/C didn't run" from "couldn't ask".
    const fetchHvacHourly = async () => {
        if (hvacSensorGroups.length === 0)
            return [];
        const [year, month, day] = earliestDate.split('-').map(Number);
        const startTimestamp = Math.floor(new Date(year, month - 1, day).getTime() / 1000);
        const endTimestamp = Math.floor(Date.now() / 1000);
        const hubIds = Array.from(new Set(hvacSensorGroups
            .map((g) => g.hubId || selectedHubId)
            .filter((id) => Boolean(id))));
        const results = await Promise.all(hubIds.map(async (hubId) => {
            try {
                const response = await amplifyBootstrap_1.API.graphql({
                    query: queries_1.getHourlyHvacEnergy,
                    variables: { hubId, startTimestamp, endTimestamp },
                });
                return (response?.data?.getHourlyHvacEnergy ?? []);
            }
            catch (err) {
                console.error(`Error fetching hourly HVAC energy for hub ${hubId}:`, err);
                return null;
            }
        }));
        if (results.every((r) => r === null))
            return null;
        const knownGroupIds = new Set(hvacSensorGroups.map((g) => g.groupId));
        return results
            .filter((r) => r !== null)
            .flat()
            .filter((row) => row?.groupId && knownGroupIds.has(row.groupId));
    };
    const fetchAll = async () => {
        if (powerMonitorSensors.length === 0) {
            setRollupsBySensor({});
            setHvacDailyRows([]);
            setHvacHourlyRows([]);
            setHvacHourlyOk(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Per-sensor catch: one flaky monitor must not blank the whole breakdown.
            const [results, hvacRows, hvacHourly] = await Promise.all([
                Promise.all(powerMonitorSensors.map(async (sensor) => {
                    try {
                        return { sensorId: sensor.id, rollups: await fetchRollupForSensor(sensor.id) };
                    }
                    catch (err) {
                        console.error(`Error fetching usage rollup for sensor ${sensor.id}:`, err);
                        return null;
                    }
                })),
                fetchHvacDailyRows(),
                fetchHvacHourly(),
            ]);
            const successes = results.filter((r) => r !== null);
            if (successes.length === 0 && powerMonitorSensors.length > 0) {
                throw new Error('Failed to fetch usage rollups for all power monitors');
            }
            const next = {};
            successes.forEach(({ sensorId, rollups }) => {
                next[sensorId] = rollups;
            });
            setRollupsBySensor(next);
            setHvacDailyRows(hvacRows);
            setHvacHourlyRows(hvacHourly ?? []);
            setHvacHourlyOk(hvacHourly !== null);
        }
        catch (err) {
            console.error('Error fetching branch cost breakdown data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch branch cost breakdown data');
            setRollupsBySensor({});
            setHvacDailyRows([]);
            setHvacHourlyRows([]);
            setHvacHourlyOk(false);
        }
        finally {
            setLoading(false);
        }
    };
    const prevFetchKeyRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        const fetchKey = `${sensorIdsKey}|${groupIdsKey}|${earliestDate}|${todayDate}`;
        if (prevFetchKeyRef.current !== fetchKey) {
            prevFetchKeyRef.current = fetchKey;
            fetchAll();
        }
    }, [sensorIdsKey, groupIdsKey, earliestDate, todayDate]);
    // Per-date and per-hour kWh maps, built once per fetch so range slicing is pure arithmetic.
    // Hour keys are the rollup `dateHour` format, `YYYY-MM-DD-HH` (Pacific).
    const daily = (0, react_1.useMemo)(() => {
        const mainsKwhByDate = new Map();
        const rowKwhByDate = new Map();
        const mainsKwhByHour = new Map();
        const rowKwhByHour = new Map();
        const rowMetaByKey = new Map();
        const addTo = (maps, at, key, kwh) => {
            const atMap = maps.get(at) ?? new Map();
            atMap.set(key, (atMap.get(key) ?? 0) + kwh);
            maps.set(at, atMap);
        };
        const addRowKwh = (date, key, kwh) => addTo(rowKwhByDate, date, key, kwh);
        for (const sensor of powerMonitorSensors) {
            const rollups = rollupsBySensor[sensor.id];
            if (!rollups?.length)
                continue;
            // Same "configured" rule as the live branch views: a circuit without a
            // location/sublocation has nothing clamped on it and is skipped.
            const circuits = (sensor.branchCircuits ?? []).filter((c) => c.sublocation || c.location);
            circuits.forEach((circuit) => {
                const key = `${sensor.id}:${circuit.powerMonitorInputNumber}`;
                if (!rowMetaByKey.has(key)) {
                    rowMetaByKey.set(key, {
                        key,
                        sensorId: sensor.id,
                        sensorName: sensor.name || sensor.location || 'Power Monitor',
                        inputNumber: circuit.powerMonitorInputNumber,
                        name: circuit.sublocation || circuit.location || `Circuit ${circuit.powerMonitorInputNumber}`,
                    });
                }
            });
            for (const rollup of rollups) {
                const date = rollup.date;
                if (!date)
                    continue;
                const usage = rollup.powerUsageRollup ?? rollup;
                const vt1 = usage?.averageVt1 || 0;
                const vt2 = usage?.averageVt2 || 0;
                // Each row is one hour of averages, so avg watts / 1000 is that hour's kWh.
                const mainsKwh = ((usage?.averageMainCt1 || 0) * vt1 + (usage?.averageMainCt2 || 0) * vt2) / 1000;
                mainsKwhByDate.set(date, (mainsKwhByDate.get(date) ?? 0) + mainsKwh);
                // Legacy rows without an hour still count toward the day; they just get no hour bar.
                const hour = rollup.hour ?? Number(rollup.dateHour?.slice(11));
                const hourKey = Number.isFinite(hour) ? `${date}-${String(hour).padStart(2, '0')}` : null;
                if (hourKey)
                    mainsKwhByHour.set(hourKey, (mainsKwhByHour.get(hourKey) ?? 0) + mainsKwh);
                for (const circuit of circuits) {
                    const input = circuit.powerMonitorInputNumber;
                    const ct = usage?.[`averageBranchCt${input}`] || 0;
                    if (!ct)
                        continue;
                    let voltage = input <= 7 ? vt1 : vt2;
                    if (circuit.voltage && circuit.voltage >= SPLIT_PHASE_VOLTAGE_THRESHOLD) {
                        voltage *= 2;
                    }
                    addRowKwh(date, `${sensor.id}:${input}`, (ct * voltage) / 1000);
                    if (hourKey)
                        addTo(rowKwhByHour, hourKey, `${sensor.id}:${input}`, (ct * voltage) / 1000);
                }
            }
        }
        // HVAC rows: one per group per day, energy from the SEER daily aggregates.
        const totalGroups = hvacSensorGroups.length;
        hvacSensorGroups.forEach((group, index) => {
            const key = `hvac:${group.groupId}`;
            // Single-system hubs just say "HVAC"; multi-system hubs prefix the location so units are
            // distinguishable — same convention as the live branch list.
            const location = group.indoorSensor?.location || group.outdoorSensor?.location;
            rowMetaByKey.set(key, {
                key,
                sensorId: group.groupId,
                sensorName: 'HVAC',
                inputNumber: 0,
                name: totalGroups === 1 ? 'HVAC' : `${location || `System ${index + 1}`} HVAC`,
                isHvac: true,
            });
        });
        const hvacDailyLanded = new Set();
        hvacDailyRows.forEach((row) => {
            if (!row.date)
                return;
            hvacDailyLanded.add(`${row.groupId}|${row.date}`);
            const kwh = (Number(row.powerWattHours) || 0) / 1000;
            if (kwh <= 0)
                return;
            addRowKwh(row.date, `hvac:${row.groupId}`, kwh);
        });
        // Hourly HVAC energy: always feeds the hour bars, and feeds the day total only where the
        // daily aggregate hasn't landed (~01:30 PT the next day) — so the two never double-count.
        // `timestamp` is the hour's start in seconds.
        hvacHourlyRows.forEach((row) => {
            const kwh = (Number(row.powerWattHours) || 0) / 1000;
            if (kwh <= 0)
                return;
            const { date, hour } = (0, pacificTime_1.pacificDateHourKey)(new Date(Number(row.timestamp) * 1000));
            const key = `hvac:${row.groupId}`;
            addTo(rowKwhByHour, `${date}-${String(hour).padStart(2, '0')}`, key, kwh);
            if (!hvacDailyLanded.has(`${row.groupId}|${date}`))
                addRowKwh(date, key, kwh);
        });
        return { mainsKwhByDate, rowKwhByDate, mainsKwhByHour, rowKwhByHour, rowMetaByKey };
    }, [rollupsBySensor, hvacDailyRows, hvacHourlyRows, powerMonitorSensors, hvacSensorGroups]);
    // Rollups land about an hour behind, so this trails the clock: "the same stretch" means
    // the hours today actually has data for, not the hours on the clock.
    const todayHoursWithData = (0, react_1.useMemo)(() => {
        let hours = 0;
        daily.mainsKwhByHour.forEach((_, key) => {
            if (key.startsWith(todayDate))
                hours = Math.max(hours, Number(key.slice(11)) + 1);
        });
        return hours;
    }, [daily, todayDate]);
    const getRangeBreakdown = (0, react_1.useCallback)((startDate, endDate, endDateHours) => {
        const { mainsKwh, kwhByKey } = (0, costSeries_1.sumRangeKwh)({ mains: daily.mainsKwhByDate, rows: daily.rowKwhByDate }, { mains: daily.mainsKwhByHour, rows: daily.rowKwhByHour }, startDate, endDate, endDateHours);
        // Rows only for keys with energy in range — a $0.00 HVAC row would falsely claim the
        // A/C cost nothing when really we couldn't attribute it (it stays in "other" instead).
        // Zero-usage circuits are equally noise on a single-day view.
        const branches = [];
        kwhByKey.forEach((kwh, key) => {
            const meta = daily.rowMetaByKey.get(key);
            if (!meta || kwh <= 0)
                return;
            branches.push({ ...meta, kwh, cost: 0, percent: 0 });
        });
        const attributedKwh = branches.reduce((sum, b) => sum + b.kwh, 0);
        // Branch CTs / HVAC estimates can slightly over-read the mains integral; the wider of
        // the two keeps percentages <= 100 instead of clamping every row individually.
        const denominatorKwh = Math.max(mainsKwh, attributedKwh);
        const otherKwh = Math.max(0, mainsKwh - attributedKwh);
        branches.forEach((b) => {
            b.cost = b.kwh * ratePerKwh;
            b.percent = denominatorKwh > 0 ? (b.kwh / denominatorKwh) * 100 : 0;
        });
        branches.sort((a, b) => b.cost - a.cost);
        return {
            branches,
            other: {
                kwh: otherKwh,
                cost: otherKwh * ratePerKwh,
                percent: denominatorKwh > 0 ? (otherKwh / denominatorKwh) * 100 : 0,
            },
            totalKwh: mainsKwh,
            totalCost: mainsKwh * ratePerKwh,
            hvacPending: hvacSensorGroups.length > 0 && endDate >= todayDate && !hvacHourlyOk,
        };
    }, [daily, ratePerKwh, hvacSensorGroups.length, todayDate, hvacHourlyOk]);
    const getSeries = (0, react_1.useCallback)((startDate, endDate, bucket, rowKey) => bucket === 'hour'
        ? (0, costSeries_1.buildCostSeries)((0, costSeries_1.hourKeysOf)(startDate), daily.mainsKwhByHour, daily.rowKwhByHour, ratePerKwh, rowKey)
        : (0, costSeries_1.buildCostSeries)((0, costSeries_1.datesInRange)(startDate, endDate), daily.mainsKwhByDate, daily.rowKwhByDate, ratePerKwh, rowKey), [daily, ratePerKwh]);
    // A power monitor is required for the mains total; the rows can come from configured branch
    // circuits, HVAC groups, or both.
    const isValidConfiguration = powerMonitorSensors.length > 0 &&
        (powerMonitorSensors.some((s) => s.branchCircuits?.some((c) => c.sublocation || c.location)) ||
            hvacSensorGroups.length > 0);
    return {
        getRangeBreakdown,
        getSeries,
        todayHoursWithData,
        earliestDate,
        todayDate,
        isValidConfiguration,
        loading,
        error,
        refetch: fetchAll,
    };
};
exports.useBranchCostBreakdown = useBranchCostBreakdown;
